/**
 * Dynamic Field Renderer
 *
 * Renders form fields based on FormField type definition
 */

import { useRef } from 'react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import { FormField } from '../../api/types';

interface FieldRendererProps {
  field: FormField;
  control: Control<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  disabled?: boolean;
}

export function FieldRenderer({ field, control, errors, disabled }: FieldRendererProps) {
  const error = errors[field.name];
  const errorMessage = error?.message as string | undefined;

  switch (field.type) {
    case 'text':
    case 'email':
      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: controllerField }) => (
            <TextField
              {...controllerField}
              value={controllerField.value || ''}
              label={field.label}
              type={field.type}
              fullWidth
              error={!!error}
              helperText={errorMessage}
              disabled={disabled}
              required={field.required}
              placeholder={field.placeholder}
              sx={{ mb: 2 }}
            />
          )}
        />
      );

    case 'textarea':
      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: controllerField }) => (
            <TextField
              {...controllerField}
              value={controllerField.value || ''}
              label={field.label}
              multiline
              rows={4}
              fullWidth
              error={!!error}
              helperText={errorMessage}
              disabled={disabled}
              required={field.required}
              placeholder={field.placeholder}
              sx={{ mb: 2 }}
            />
          )}
        />
      );

    case 'number':
      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: controllerField }) => (
            <TextField
              {...controllerField}
              value={controllerField.value || ''}
              label={field.label}
              type="number"
              fullWidth
              error={!!error}
              helperText={errorMessage}
              disabled={disabled}
              required={field.required}
              placeholder={field.placeholder}
              inputProps={{
                min: field.validation?.min,
                max: field.validation?.max,
              }}
              sx={{ mb: 2 }}
            />
          )}
        />
      );

    case 'date':
      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: controllerField }) => (
            <TextField
              {...controllerField}
              value={controllerField.value || ''}
              label={field.label}
              type="date"
              fullWidth
              error={!!error}
              helperText={errorMessage}
              disabled={disabled}
              required={field.required}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
          )}
        />
      );

    case 'select':
      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: controllerField }) => (
            <TextField
              {...controllerField}
              value={controllerField.value || ''}
              select
              label={field.label}
              fullWidth
              error={!!error}
              helperText={errorMessage}
              disabled={disabled}
              required={field.required}
              sx={{ mb: 2 }}
              SelectProps={{ native: true }}
            >
              <option value=""></option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
          )}
        />
      );

    case 'checkbox':
      return (
        <FormControl error={!!error} sx={{ mb: 2, display: 'block' }}>
          <Controller
            name={field.name}
            control={control}
            render={({ field: controllerField }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!controllerField.value}
                    onChange={controllerField.onChange}
                    onBlur={controllerField.onBlur}
                    name={controllerField.name}
                    disabled={disabled}
                  />
                }
                label={field.label}
              />
            )}
          />
          {error && <FormHelperText>{errorMessage}</FormHelperText>}
        </FormControl>
      );

    case 'file':
      return (
        <FileFieldRenderer
          field={field}
          control={control}
          error={error}
          errorMessage={errorMessage}
          disabled={disabled}
        />
      );

    default:
      return null;
  }
}

function FileFieldRenderer({
  field,
  control,
  error,
  errorMessage,
  disabled,
}: {
  field: FormField;
  control: Control<Record<string, unknown>>;
  error: FieldErrors<Record<string, unknown>>[string];
  errorMessage: string | undefined;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <FormControl error={!!error} sx={{ mb: 2, display: 'block' }}>
      <Controller
        name={field.name}
        control={control}
        render={({ field: controllerField }) => {
          const file = controllerField.value as File | null;
          return (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {field.label}
                {field.required && ' *'}
              </Typography>
              <input
                ref={inputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const selected = e.target.files?.[0] || null;
                  controllerField.onChange(selected);
                }}
                disabled={disabled}
                data-testid={`file-input-${field.name}`}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => inputRef.current?.click()}
                  disabled={disabled}
                >
                  Datei auswählen
                </Button>
                {file && (
                  <Typography variant="body2" color="text.primary">
                    {file.name}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
      />
      {error && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
}
